import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './WorkItemDefinition.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Work Item Definition.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/deliverables.ejs`.
 *
 * NOT `definition.ejs`. That file is "Roadmap Definition — Execution", which the owner moved
 * out of this port's scope. This one's own `<h1>` reads "Work Item Definition", and every
 * screen that references it links to `/roadmap-execution/deliverables`.
 *
 * WHAT IT DOES, in the source's words: decide what gets produced on this program. Work
 * top-down — first the program-wide Global work items that exist once for the whole
 * engagement, then each opportunity's own work items by phase, as Waterfall (five phases)
 * or Agile. Everything marked in scope here becomes the master list that drives Assignment,
 * RACI, budget/resource tracking and the Control Center.
 *
 * A THREE-LEVEL TREE GRID in one table: scope → phase/MVP wave → work item. The indicator
 * column carries the caret on group rows; on a work-item row the in-scope checkbox sits
 * INSIDE the name column, indented under its phase. The source's note saying so is carried
 * into the CSS.
 *
 * WATERFALL SHOWS THE FIVE PHASES, AGILE SHOWS MVP WAVES — `phaseVisible` is the whole rule
 * and it runs both ways: an Agile opportunity shows only `Agile_Delivery`, a Waterfall one
 * shows everything except it. Switching the toggle re-groups without touching the data.
 *
 * THE FILTER REVEALS ACROSS THE WHOLE TREE. While filtering, every group renders expanded
 * regardless of its collapsed state, groups with no match are omitted entirely, and the
 * match is highlighted. Clearing it returns to the stored expansion. That is why `render`
 * reads `NARROWED ? true : expanded.has(...)` rather than mutating the expansion sets —
 * a filter must not silently rearrange what the user had open.
 *
 * UNTICKING A STAFFED WORK ITEM IS DESTRUCTIVE, and the source says so before doing it:
 * out-of-scope means the row and its assignees are DELETED on save, not flagged. The
 * confirm, its exact wording and the warning toast are reproduced, because the warning is
 * the only thing standing between a stray click and someone's assignments.
 *
 * `alert()` becomes an inline line, as elsewhere in this module; `confirm()` is kept, since
 * it guards that deletion.
 */

interface WorkItem {
  id?: number;
  is_custom?: boolean;
  catalog_key?: string | null;
  phase: string;
  name: string;
  description?: string | null;
  file_type?: string | null;
  work_item_type?: string | null;
  owner?: string | null;
  in_scope?: boolean;
  streams?: string[];
  document_link?: string | null;
  delivered_by_date?: string | null;
  /** §7.2 — a work item runs FROM a date TO a date; only the end used to exist. */
  start_date?: string | null;
  mvp_seq?: number | null;
  /** How many people are already assigned on Work Item Assignment. */
  assigned?: number;
}

interface PhaseRef {
  key: string;
  label: string;
}

interface Mvp {
  seq: number;
  name: string;
  start?: string;
  end?: string;
  sprintCount?: number;
}

interface Opportunity {
  opportunity_id: string;
  name?: string | null;
  methodology?: string | null;
  phases?: PhaseRef[];
  agile_mvps?: Mvp[];
  items: WorkItem[];
  /* THIS initiative's own phase window. The default for its work items' dates, because
     initiatives under one programme run at different times — §7.2. */
  phase_starts?: Record<string, string>;
  phase_ends?: Record<string, string>;
}

interface ProgramData {
  success?: boolean;
  error?: string;
  file_type_options: string[];
  owner_options: string[];
  work_item_type_options?: string[];
  project_streams?: string[];
  phase_ends?: Record<string, string>;
  phase_starts?: Record<string, string>;
  global: { items: WorkItem[] };
  opportunities: Opportunity[];
}

/* Shared phase design language — matches the Work Item Assignment screen. */
const PHASE_LABEL: Record<string, string> = {
  Assessment_Planning: 'Assessment & Planning',
  Design_Architecture: 'Design & Architecture',
  Development: 'Development',
  Testing_Validation: 'Testing & Validation',
  GoLive_Optimization: 'Go-Live & Optimization',
  Agile_Delivery: 'Agile Delivery',
};
const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};
const MVP_COLORS = [
  '#00838f', '#1565c0', '#6a1b9a', '#ef6c00', '#2e7d32',
  '#5e35b1', '#c62828', '#00897b', '#7cb342', '#3949ab',
];

const mvpSeqOf = (it: WorkItem) => (it.mvp_seq == null ? 0 : it.mvp_seq);

/**
 * WHO GETS PHASES — mirrors `phaseRules.ts` on the server, which is the authority.
 *
 * ONLY Waterfall gets the five phases. ONLY Agile gets MVP waves. Everything else — an
 * explicit "None" AND an initiative with no methodology chosen yet — gets the single flat
 * Delivery bucket.
 *
 * The previous rule was `Agile ? agile : everything else`, which quietly handed the full
 * five-phase Waterfall plan to every undecided initiative, inventing a delivery lifecycle
 * for work that has none.
 *
 * The server already filters the payload, so this is belt-and-braces for anything the client
 * groups locally — not the rule itself. The rule lives on the server so two screens cannot
 * answer the same question differently.
 */
const FLAT_PHASE = 'Flat_Delivery';
const isFlatMeth = (meth?: string | null) => {
  const m = (meth ?? '').trim();
  return m === '' || m === 'None';
};
const phaseVisible = (phaseKey: string, meth?: string | null) =>
  meth === 'Agile'
    ? phaseKey === 'Agile_Delivery'
    : isFlatMeth(meth)
      ? phaseKey === FLAT_PHASE
      : phaseKey !== 'Agile_Delivery' && phaseKey !== FLAT_PHASE;

/** `hl(text)` — the match, marked. */
function Highlight({ text, term }: { text: string; term: string }) {
  const t = text ?? '';
  if (!term) return <>{t}</>;
  const i = t.toLowerCase().indexOf(term);
  if (i < 0) return <>{t}</>;
  return (
    <>
      {t.slice(0, i)}
      <mark className="dl-hl">{t.slice(i, i + term.length)}</mark>
      {t.slice(i + term.length)}
    </>
  );
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/deliverables.ejs`,
 * its `deliverablesHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const WID_HELP: HelpContent = {
    title: 'Work Item Definition',
    icon: '📦',
    purpose: "Decide <strong>what gets produced</strong> on this project. Work top&#8209;down: first the project&#8209;wide <strong>Global</strong> work items that exist once for the whole engagement &mdash; these have <strong>no phases</strong> &mdash; then <strong>each opportunity's</strong> own work items by phase, as <strong>Waterfall</strong> (five phases) or <strong>Agile</strong>, or with <strong>no phases at all</strong> if its methodology is <strong>None</strong>. Everything you mark <strong>in scope</strong> here becomes the master list that drives <strong>Assignment</strong>, <strong>RACI</strong>, budget/resource tracking and the <strong>Control Center</strong>.",
    doHere: [
      "Pick a <strong>Project</strong> (delivery project); its opportunities load beneath a Global section, all collapsed.",
      "Use the <strong>Show</strong> toggle to switch between <strong>All available</strong> (the whole catalog, for choosing work) and <strong>In scope only</strong> (just what this project has taken on, for reading the plan). The screen <strong>opens on In scope only</strong> once a project has chosen any work, so you see the plan rather than the catalog; a project with nothing in scope yet opens on the catalog.",
      "Use the <strong>Filter work items</strong> box (next to the project picker) to type any part of a work item name &mdash; matching rows are revealed across every opportunity, phase and MVP wave, with the match highlighted. Clear it to return to the normal collapsed view.",
      "In <strong>🌐 Global Work Items</strong>, tick the project&#8209;level artifacts (Project Charter, Business Case, Governance, Budget&hellip;) &mdash; produced once for the whole project, in one flat list with no phases.",
      "Open each <strong>opportunity</strong>, set it <strong>Waterfall</strong> or <strong>Agile</strong>, then tick what's in scope (or <strong>Select all</strong> per group). <strong>Waterfall</strong> groups by the five phases; <strong>Agile</strong> groups by <strong>MVP wave</strong> (from the cadence on Roadmap Definition) &mdash; use each row's <strong>MVP wave</strong> picker to move a work item to another wave.",
      "Per row, set the <strong>File Type</strong>, <strong>Typical Owner</strong>, <strong>Stream</strong>, <strong>Start Date</strong> and <strong>Delivered By</strong> date, and add <strong>custom</strong> work items where the catalog doesn't cover something.",
      "Remove a work item with the <strong>&#10005;</strong> on its row. On <strong>Save</strong> the project's copy is <strong>deleted from the database</strong>, along with anyone assigned to it, so it disappears from Assignment, RACI and tracking too. A catalog work item stays available to re-add later from <strong>All available</strong>; a <strong>custom</strong> one is gone for good.",
      "<strong>Save</strong> &mdash; only in&#8209;scope work items flow downstream to Assignment, RACI and tracking."
    ],
    fits: 'Stream Definition &rarr; <strong>Work Item Definition</strong> &rarr; Work Item Assignment &rarr; RACI Definition &rarr; Execution Control Center',
    firstTime: "Pick a project, tick a few Global work items, then expand one opportunity, choose Waterfall, open <em>Assessment &amp; Planning</em>, <strong>Select all</strong>, and Save.",
    sections: [
      {
        label: 'How the list is structured',
        type: 'defs',
        items: [
          { term: 'Global', color: '#6a1b9a', def: "Project&#8209;level work items produced <strong>once</strong> for the whole project (charter, business case, governance). They belong to no opportunity, so they carry <strong>no methodology and no phases</strong> &mdash; treated exactly like an opportunity set to <strong>None</strong>, they are one flat list. They stay available inside opportunities too &mdash; nothing is excluded." },
          { term: 'Opportunity', color: '#1976d2', def: "Each opportunity carries its <strong>own</strong> work item set and its own <strong>Waterfall / Agile</strong> choice (set here or on the Definition screen) &mdash; so one project can mix both." },
          { term: 'Phase / MVP wave', color: '#42a5f5', def: "Inside an opportunity, work items are grouped by delivery <strong>phase</strong> (Waterfall) or by <strong>MVP wave</strong> (Agile &mdash; each wave shows its date window and sprint count). Each group has its own count and Select&#8209;all, and Agile rows carry an <strong>MVP wave</strong> picker." },
          { term: 'Stream', color: '#1565c0', def: "Tag a work item to a business/technical <strong>stream</strong> (Order to Cash, Development&hellip;) or leave it <em>Global</em> (central). Streams come from the Stream Definition screen." },
          { term: 'Custom', color: '#2e7d32', def: "Add your own rows when the catalog is missing something &mdash; give them a name, file type and owner like any catalog item." }
        ]
      },
      {
        label: 'The five Waterfall phases',
        type: 'defs',
        items: [
          { term: 'Assessment', color: '#1e88e5', def: "Assessment &amp; Planning &mdash; scope, plan, mobilization, governance." },
          { term: 'Design', color: '#8e24aa', def: "Design &amp; Architecture &mdash; requirements, fit&#8209;gap, blueprint, specs." },
          { term: 'Development', color: '#2e7d32', def: "Build &mdash; configuration, custom code, integrations, data migration." },
          { term: 'Testing', color: '#ef6c00', def: "Testing &amp; Validation &mdash; test plans, SIT / UAT, defects, sign&#8209;off." },
          { term: 'Go-Live', color: '#d32f2f', def: "Go&#8209;Live &amp; Optimization &mdash; cutover, hypercare, handover, closure." }
        ],
        note: "Set an opportunity to <strong>Agile</strong> and it swaps the Waterfall phases for the <em>Agile Delivery</em> set (product backlog, sprints, increments)."
      },
      {
        label: 'Start Date & Delivered By',
        type: 'html',
        html: "Both dates default from the <strong>phase window of the initiative the work item belongs to</strong> &mdash; <strong>Start Date</strong> from that phase's start, <strong>Delivered By</strong> from its end. Initiatives under one project run at different times, so the same catalog work item legitimately carries different dates under each. Override either date per row; the project-level window is used only where an initiative has no dates of its own."
      },
      {
        label: 'File Type, Owner & Stream',
        type: 'html',
        html: "Every catalog work item ships with a sensible default <strong>File Type</strong> (Word, Excel, PowerPoint&hellip;) and <strong>Typical Owner</strong> (Project Manager, Test Lead&hellip;) &mdash; all editable dropdowns you can override per row. <strong>Stream</strong> defaults to <em>Global (all)</em>; choose a stream to make that work item stream&#8209;specific, so one opportunity can carry an <em>Order to Cash</em> instance while another does not."
      },
      {
        label: 'Adding & removing work items',
        type: 'html',
        html: "Scope changes flow straight through to <strong>Work Item Assignment</strong>.<br><br><strong>Adding a work item</strong> &mdash; tick a catalog item, or use <em>+ Add custom work item</em> for something the catalog doesn't have. A brief info note confirms it; once you <strong>Save</strong>, the work item appears on the Assignment screen for its phase and stream, ready to have people assigned. No warning &mdash; adding is safe.<br><br><strong>Removing a work item</strong> &mdash; un&#8209;tick it. If it <em>already has people assigned</em>, you get a <strong>confirmation warning</strong> first, because this is destructive: on <strong>Save</strong> the work item <strong>and its assignment(s) are permanently deleted</strong> &mdash; from this phase, from the Assignment screen, and from the database. (Choose <em>Cancel</em> to keep it.) Removing a work item that has <em>no</em> assignees just takes it out of scope, with no warning."
      }
    ],
    links: [
      { label: 'Next: Work Item Assignment →', href: '/project_management/program-management/scope-ownership/work-item-assignment' },
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' }
    ]
  };

export function WorkItemDefinition() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [prog, setProg] = useState<ProgramData | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [phaseExpanded, setPhaseExpanded] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'info' | 'warn' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  /*
   * SCOPE VIEW — §7.1. Two different jobs get a toggle rather than one crowded list:
   *
   *   all      the whole catalogue pool to pick from   (default — you arrive here to choose)
   *   scoped   just what this programme has taken on   (reading the plan)
   *
   * It folds into the SAME `NARROWED` predicate the text filter already uses, so a group with
   * no visible rows is hidden and what remains opens automatically — one behaviour, learned
   * once, rather than two narrowing mechanisms that behave differently.
   */
  const [scopeView, setScopeView] = useState<'all' | 'scoped'>('all');
  const SCOPED = scopeView === 'scoped';

  const term = filterText.trim().toLowerCase();
  const FILTERING = term.length > 0;
  /** Anything that narrows the tree — text, scope, or both. */
  const NARROWED = FILTERING || SCOPED;
  const itemMatches = (it: WorkItem) => {
    if (SCOPED && !it.in_scope) return false;
    if (!FILTERING) return true;
    return (
      String(it.name || '').toLowerCase().includes(term) ||
      String(it.description || '').toLowerCase().includes(term)
    );
  };

  const dlToast = useCallback((msg: string, kind: 'info' | 'warn') => {
    setToast({ msg, kind });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ projects: PmProject[] }>('/deliverables/projects');
        setProjects(d.projects ?? []);
      } catch {
        /* the source logs and leaves the picker empty */
      }
    })();
  }, []);

  const onSelectProject = async (id: string) => {
    setProjectId(id);
    setFilterText('');
    setNotice(null);
    if (!id) {
      setProg(null);
      return;
    }
    try {
      const d = await pmGet<ProgramData>(
        `/deliverables/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      /*
        Default each work item's dates from its PHASE WINDOW — start from the phase start,
        Delivered By from the phase end. Global uses the Design & Architecture window.
        Done once on load, as the source does: at render it would keep refilling a date
        somebody deliberately cleared.

        PER INITIATIVE, NOT PER PROGRAMME — §7.2. Initiatives under one programme run at
        different times, so the same catalogue work item legitimately carries different dates
        under each. The programme window is only the fallback for an initiative that has none.
      */
      const ends = d.phase_ends ?? {};
      const starts = d.phase_starts ?? {};
      const gEnd = ends['Design_Architecture'] || '';
      const gStart = starts['Design_Architecture'] || '';
      d.global.items.forEach((it) => {
        if (!it.delivered_by_date) it.delivered_by_date = gEnd;
        if (!it.start_date) it.start_date = gStart;
      });
      (d.opportunities ?? []).forEach((o) =>
        o.items.forEach((it) => {
          if (!it.delivered_by_date) {
            it.delivered_by_date = (o.phase_ends ?? {})[it.phase] || ends[it.phase] || '';
          }
          if (!it.start_date) {
            it.start_date = (o.phase_starts ?? {})[it.phase] || starts[it.phase] || '';
          }
        }),
      );
      setExpanded(new Set());
      setPhaseExpanded(new Set());
      setProg(d);
      setDirty(false);
      setSaveLabel('Save');
    } catch {
      setNotice('Failed to load program work items');
    }
  };

  /* ── Mutating helpers. Each clones the path React must see change. ────── */

  const mutateScope = (scopeId: string, fn: (items: WorkItem[]) => WorkItem[]) => {
    setProg((cur) => {
      if (!cur) return cur;
      if (scopeId === 'global') return { ...cur, global: { items: fn(cur.global.items) } };
      return {
        ...cur,
        opportunities: cur.opportunities.map((o) =>
          `opp:${o.opportunity_id}` === scopeId ? { ...o, items: fn(o.items) } : o,
        ),
      };
    });
    setDirty(true);
    setSaveLabel('Save');
  };

  const setField = (scopeId: string, idx: number, field: keyof WorkItem, value: unknown) =>
    mutateScope(scopeId, (items) =>
      items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)),
    );

  const scopeItems = (scopeId: string): WorkItem[] => {
    if (!prog) return [];
    if (scopeId === 'global') return prog.global.items;
    return prog.opportunities.find((o) => `opp:${o.opportunity_id}` === scopeId)?.items ?? [];
  };

  const scopeMethodology = (scopeId: string): string | null => {
    if (!prog || scopeId === 'global') return null;
    return (
      prog.opportunities.find((o) => `opp:${o.opportunity_id}` === scopeId)?.methodology ??
      'Waterfall'
    );
  };

  /**
   * Ticking scope off on a staffed work item deletes it AND its assignees on save.
   * The confirm and the toast are the source's, word for word.
   */
  const toggleInScope = (scopeId: string, idx: number, checked: boolean) => {
    const it = scopeItems(scopeId)[idx];
    if (!it) return;
    if (!checked && (it.assigned || 0) > 0) {
      const n = it.assigned!;
      const ok = window.confirm(
        `⚠️  "${it.name}" already has ${n} person${n > 1 ? 's' : ''} assigned on Work Item Assignment.\n\n` +
          `Removing it from scope will delete the work item and its assignment${n > 1 ? 's' : ''}. Continue?`,
      );
      if (!ok) return;
      mutateScope(scopeId, (items) =>
        items.map((x, i) => (i === idx ? { ...x, assigned: 0, in_scope: false } : x)),
      );
      dlToast(
        `Removed — this work item and its ${n} assignment${n > 1 ? 's' : ''} will be deleted when you Save.`,
        'warn',
      );
      return;
    }
    setField(scopeId, idx, 'in_scope', checked);
  };

  const setStream = (scopeId: string, idx: number, val: string) =>
    setField(scopeId, idx, 'streams', val ? [val] : []);

  const setMvpSeq = (scopeId: string, idx: number, val: string) => {
    mutateScope(scopeId, (items) =>
      items.map((it, i) => (i === idx ? { ...it, mvp_seq: val === '' ? null : Number(val) } : it)),
    );
    // Keep the row visible where it lands: open the scope and every wave in it.
    setExpanded((cur) => new Set(cur).add(scopeId));
    const op = prog?.opportunities.find((o) => `opp:${o.opportunity_id}` === scopeId);
    if (op) {
      setPhaseExpanded((cur) => {
        const next = new Set(cur);
        (op.agile_mvps ?? []).forEach((m) => next.add(`${scopeId}::mvp-${m.seq}`));
        return next;
      });
    }
  };

  const setOppMethodology = (oppId: string, meth: string) => {
    setProg((cur) => {
      if (!cur) return cur;
      const op = cur.opportunities.find((o) => String(o.opportunity_id) === String(oppId));
      if (!op || op.methodology === meth) return cur;
      return {
        ...cur,
        opportunities: cur.opportunities.map((o) =>
          String(o.opportunity_id) === String(oppId) ? { ...o, methodology: meth } : o,
        ),
      };
    });
    setExpanded((cur) => new Set(cur).add(`opp:${oppId}`));
    setDirty(true);
    setSaveLabel('Save');
  };

  const selectAllScope = (scopeId: string, val: boolean) => {
    const meth = scopeMethodology(scopeId);
    mutateScope(scopeId, (items) =>
      items.map((it) =>
        scopeId === 'global' || phaseVisible(it.phase, meth) ? { ...it, in_scope: val } : it,
      ),
    );
  };
  const selectAllPhase = (scopeId: string, phaseKey: string, val: boolean) =>
    mutateScope(scopeId, (items) =>
      items.map((it) => (it.phase === phaseKey ? { ...it, in_scope: val } : it)),
    );
  const selectAllMvp = (scopeId: string, seq: number, val: boolean) =>
    mutateScope(scopeId, (items) =>
      items.map((it) =>
        it.phase === 'Agile_Delivery' && mvpSeqOf(it) === seq ? { ...it, in_scope: val } : it,
      ),
    );

  const addCustom = (scopeId: string, phaseKey: string, mvpSeq?: number) => {
    const ends = prog?.phase_ends ?? {};
    const dflt = scopeId === 'global' ? ends['Design_Architecture'] || '' : ends[phaseKey] || '';
    const item: WorkItem = {
      is_custom: true,
      catalog_key: null,
      phase: phaseKey,
      name: '',
      description: '',
      file_type: 'Word',
      owner: '',
      in_scope: true,
      streams: [],
      document_link: '',
      delivered_by_date: dflt,
      assigned: 0,
      ...(mvpSeq != null ? { mvp_seq: mvpSeq } : {}),
    };
    mutateScope(scopeId, (items) => [...items, item]);
    setExpanded((cur) => new Set(cur).add(scopeId));
    setPhaseExpanded((cur) =>
      new Set(cur).add(`${scopeId}::${mvpSeq != null ? `mvp-${mvpSeq}` : phaseKey}`),
    );
    dlToast(
      'New work item added — once saved it appears on Work Item Assignment; assign people to it there.',
      'info',
    );
  };

  const removeItem = (scopeId: string, idx: number) =>
    mutateScope(scopeId, (items) => items.filter((_, i) => i !== idx));

  const toggleScope = (scopeId: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(scopeId)) next.delete(scopeId);
      else next.add(scopeId);
      return next;
    });
  const togglePhase = (gid: string) =>
    setPhaseExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });

  const expandAll = (open: boolean) => {
    if (!prog) return;
    if (!open) {
      setExpanded(new Set());
      setPhaseExpanded(new Set());
      return;
    }
    const sc = new Set<string>(['global']);
    const ph = new Set<string>();
    prog.opportunities.forEach((o) => {
      const sid = `opp:${o.opportunity_id}`;
      sc.add(sid);
      if (o.methodology === 'Agile') {
        (o.agile_mvps ?? []).forEach((m) => ph.add(`${sid}::mvp-${m.seq}`));
      } else {
        (o.phases ?? []).forEach((p) => {
          if (phaseVisible(p.key, o.methodology)) ph.add(`${sid}::${p.key}`);
        });
      }
    });
    setExpanded(sc);
    setPhaseExpanded(ph);
  };

  const save = async () => {
    if (!projectId || !prog) return;
    setSaveLabel('Saving…');
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/deliverables/program-save', {
        delivery_project_id: projectId,
        global: prog.global.items,
        opportunities: prog.opportunities.map((o) => ({
          opportunity_id: o.opportunity_id,
          methodology: o.methodology,
          items: o.items,
        })),
      });
      if (!d.success) {
        setNotice(`Error: ${d.error || 'failed to save'}`);
        setSaveLabel('Save');
        return;
      }
      // Re-read: the server deletes out-of-scope rows, so the ids change.
      const fresh = await pmGet<ProgramData>(
        `/deliverables/program-data?delivery_project_id=${encodeURIComponent(projectId)}`,
      );
      setProg(fresh);
      setDirty(false);
      setSaveLabel('Saved ✓');
      window.setTimeout(() => setSaveLabel('Save'), 1100);
    } catch {
      setNotice('Request failed');
      setSaveLabel('Save');
    }
  };

  /* ── Rendering ────────────────────────────────────────────────────────── */

  const anyAgile = !!prog?.opportunities.some((o) => o.methodology === 'Agile');
  /* One more column since §7.2 added Start Date — 10 with the MVP wave column, 9 without. */
  const cols = anyAgile ? 10 : 9;

  const inScopeTotal = prog
    ? prog.global.items.filter((i) => i.in_scope).length +
      prog.opportunities.reduce((n, o) => n + o.items.filter((i) => i.in_scope).length, 0)
    : 0;

  const matchCount = prog
    ? prog.global.items.filter(itemMatches).length +
      prog.opportunities.reduce(
        (n, o) =>
          n + o.items.filter((it) => phaseVisible(it.phase, o.methodology) && itemMatches(it)).length,
        0,
      )
    : 0;

  const ownerOptions = (cur?: string | null) => {
    const opts = (prog?.owner_options ?? []).slice();
    if (cur && !opts.includes(cur)) opts.unshift(cur);
    if (!cur) opts.unshift('');
    return opts;
  };

  /** One work-item row. `depth` is the source's 22 / 42px indent. */
  const itemRow = (scopeId: string, it: WorkItem, idx: number, mvps: Mvp[] | null, depth: number) => (
    <tr className="dlv-row" key={`${scopeId}-${idx}`}>
      <td className="ind" />
      <td>
        <div className="dlv-name-cell" style={{ paddingLeft: depth === 1 ? 22 : 42 }}>
          <input
            type="checkbox"
            checked={!!it.in_scope}
            onChange={(e) => toggleInScope(scopeId, idx, e.target.checked)}
            aria-label={it.name || 'Custom work item'}
          />
          {it.is_custom ? (
            <input
              className="dl-input"
              placeholder="Custom work item name"
              value={it.name}
              onChange={(e) => setField(scopeId, idx, 'name', e.target.value)}
            />
          ) : (
            <div style={{ minWidth: 0 }}>
              <div className="dl-name">
                <Highlight text={it.name} term={term} />
              </div>
              {it.description ? (
                <div className="dl-desc">
                  <Highlight text={it.description} term={term} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </td>
      <td>
        <select
          className="dl-input"
          value={it.work_item_type || 'Deliverable'}
          onChange={(e) => setField(scopeId, idx, 'work_item_type', e.target.value)}
        >
          {(prog?.work_item_type_options ?? ['Deliverable', 'Activity']).map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </td>
      {anyAgile ? (
        <td>
          {mvps ? (
            <select
              className="dl-input"
              value={String(mvpSeqOf(it))}
              onChange={(e) => setMvpSeq(scopeId, idx, e.target.value)}
            >
              {mvps.map((m) => (
                <option key={m.seq} value={m.seq}>
                  {m.name}
                </option>
              ))}
            </select>
          ) : null}
        </td>
      ) : null}
      <td>
        <select
          className="dl-input"
          value={it.file_type ?? ''}
          onChange={(e) => setField(scopeId, idx, 'file_type', e.target.value)}
        >
          {(prog?.file_type_options ?? []).map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </td>
      <td>
        <select
          className="dl-input"
          value={it.owner ?? ''}
          onChange={(e) => setField(scopeId, idx, 'owner', e.target.value)}
        >
          {ownerOptions(it.owner).map((o, i) => (
            <option key={`${o}-${i}`} value={o}>
              {o}
            </option>
          ))}
        </select>
      </td>
      <td>
        {(prog?.project_streams ?? []).length === 0 ? (
          <span className="chip">Global</span>
        ) : (
          <select
            className="dl-input"
            value={it.streams && it.streams.length ? it.streams[0] : ''}
            onChange={(e) => setStream(scopeId, idx, e.target.value)}
          >
            <option value="">Global (all)</option>
            {(prog?.project_streams ?? []).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        )}
      </td>
      <td>
        <input
          type="date"
          className="dl-input"
          value={it.start_date ?? ''}
          onChange={(e) => setField(scopeId, idx, 'start_date', e.target.value)}
        />
      </td>
      <td>
        <input
          type="date"
          className="dl-input"
          value={it.delivered_by_date ?? ''}
          onChange={(e) => setField(scopeId, idx, 'delivered_by_date', e.target.value)}
        />
      </td>
      <td>
        {it.is_custom ? (
          <button
            type="button"
            className="dl-rm"
            title="Remove"
            onClick={() => removeItem(scopeId, idx)}
          >
            ✕
          </button>
        ) : null}
      </td>
    </tr>
  );

  const groupActions = (children: React.ReactNode) => (
    <span className="grp-actions" onClick={(e) => e.stopPropagation()}>
      {children}
    </span>
  );

  const rows: React.ReactNode[] = [];

  if (prog) {
    /* ── Global ───────────────────────────────────────────────────────── */
    const gItems = prog.global.items;
    const gMatching: [WorkItem, number][] = [];
    gItems.forEach((it, idx) => {
      if (itemMatches(it)) gMatching.push([it, idx]);
    });
    if (!(NARROWED && !gMatching.length)) {
      const open = NARROWED ? true : expanded.has('global');
      rows.push(
        <tr className="scope-row" key="global" onClick={() => toggleScope('global')}>
          <td className="ind">
            <i className="material-icons gcaret" style={{ transform: `rotate(${open ? 90 : 0}deg)` }}>
              chevron_right
            </i>
          </td>
          <td colSpan={cols - 1}>
            <div className="grp-cell lvl0">
              <span className="dl-dot" style={{ background: '#6a1b9a' }} />
              🌐 Global Work Items
              <span className="dl-pcount">{gItems.filter((i) => i.in_scope).length} in scope</span>
              {groupActions(
                <>
                  <button type="button" className="link-btn" onClick={() => selectAllScope('global', true)}>
                    Select all
                  </button>
                  <button type="button" className="link-btn" onClick={() => selectAllScope('global', false)}>
                    Clear
                  </button>
                  {!NARROWED ? (
                    <button
                      type="button"
                      className="dl-add"
                      onClick={() => addCustom('global', 'Assessment_Planning')}
                    >
                      + Add global work item
                    </button>
                  ) : null}
                </>,
              )}
            </div>
          </td>
        </tr>,
      );
      if (open) gMatching.forEach(([it, idx]) => rows.push(itemRow('global', it, idx, null, 1)));
    }

    /* ── Each opportunity ─────────────────────────────────────────────── */
    prog.opportunities.forEach((op) => {
      const scopeId = `opp:${op.opportunity_id}`;
      const isAgile = op.methodology === 'Agile';
      const open = NARROWED ? true : expanded.has(scopeId);
      const child: React.ReactNode[] = [];
      let anyMatch = false;

      if (isAgile) {
        const mvps = op.agile_mvps ?? [];
        if (!mvps.length) {
          if (open && !NARROWED) {
            child.push(
              <tr className="dlv-row" key={`${scopeId}-nowaves`}>
                <td className="ind" />
                <td colSpan={cols - 1} style={{ padding: '14px 18px', color: '#78909c', fontSize: 13, lineHeight: 1.7 }}>
                  This Agile opportunity has <strong>no MVP waves</strong> yet, so its work items
                  can&apos;t be organised into waves. Plan its <strong>MVP waves &amp; backlog</strong> in
                  the Agile Delivery Workspace (waves come from the <strong>MVP cadence</strong> on
                  Project Creation).
                </td>
              </tr>,
            );
          }
        } else {
          mvps.forEach((m) => {
            const gid = `${scopeId}::mvp-${m.seq}`;
            const matching: [WorkItem, number][] = [];
            op.items.forEach((it, idx) => {
              if (it.phase === 'Agile_Delivery' && mvpSeqOf(it) === m.seq && itemMatches(it))
                matching.push([it, idx]);
            });
            if (NARROWED && !matching.length) return;
            anyMatch = true;
            const popen = NARROWED ? true : phaseExpanded.has(gid);
            const n = op.items.filter(
              (i) => i.phase === 'Agile_Delivery' && mvpSeqOf(i) === m.seq && i.in_scope,
            ).length;
            if (!open) return;
            child.push(
              <tr className="phase-row" key={gid} onClick={() => togglePhase(gid)}>
                <td className="ind">
                  <i className="material-icons gcaret sm" style={{ transform: `rotate(${popen ? 90 : 0}deg)` }}>
                    chevron_right
                  </i>
                </td>
                <td colSpan={cols - 1}>
                  <div className="grp-cell lvl1">
                    <span className="ph-box" style={{ background: MVP_COLORS[m.seq % MVP_COLORS.length] }} />
                    {`🔁 ${m.name}`}
                    {m.start || m.end ? (
                      <span className="ph-win">
                        {`${m.start || ''} → ${m.end || ''}`}
                        {m.sprintCount ? ` · ${m.sprintCount} sprint${m.sprintCount === 1 ? '' : 's'}` : ''}
                      </span>
                    ) : null}
                    <span className="dl-pcount">{n} in scope</span>
                    {groupActions(
                      <>
                        <button type="button" className="link-btn" onClick={() => selectAllMvp(scopeId, m.seq, true)}>
                          Select all
                        </button>
                        <button type="button" className="link-btn" onClick={() => selectAllMvp(scopeId, m.seq, false)}>
                          Clear
                        </button>
                        {!NARROWED ? (
                          <button
                            type="button"
                            className="dl-add"
                            onClick={() => addCustom(scopeId, 'Agile_Delivery', m.seq)}
                          >
                            + Add work item
                          </button>
                        ) : null}
                      </>,
                    )}
                  </div>
                </td>
              </tr>,
            );
            if (popen)
              matching.forEach(([it, idx]) => child.push(itemRow(scopeId, it, idx, op.agile_mvps ?? [], 2)));
          });
        }
      } else {
        (op.phases ?? [])
          .filter((p) => phaseVisible(p.key, op.methodology))
          .forEach((p) => {
            const gid = `${scopeId}::${p.key}`;
            const matching: [WorkItem, number][] = [];
            op.items.forEach((it, idx) => {
              if (it.phase === p.key && itemMatches(it)) matching.push([it, idx]);
            });
            if (NARROWED && !matching.length) return;
            anyMatch = true;
            const popen = NARROWED ? true : phaseExpanded.has(gid);
            const n = op.items.filter((i) => i.phase === p.key && i.in_scope).length;
            if (!open) return;
            child.push(
              <tr className="phase-row" key={gid} onClick={() => togglePhase(gid)}>
                <td className="ind">
                  <i className="material-icons gcaret sm" style={{ transform: `rotate(${popen ? 90 : 0}deg)` }}>
                    chevron_right
                  </i>
                </td>
                <td colSpan={cols - 1}>
                  <div className="grp-cell lvl1">
                    <span className="ph-box" style={{ background: PHASE_COLORS[p.key] || '#b0bec5' }} />
                    {PHASE_LABEL[p.key] || p.label}
                    <span className="dl-pcount">{n} in scope</span>
                    {groupActions(
                      <>
                        <button type="button" className="link-btn" onClick={() => selectAllPhase(scopeId, p.key, true)}>
                          Select all
                        </button>
                        <button type="button" className="link-btn" onClick={() => selectAllPhase(scopeId, p.key, false)}>
                          Clear
                        </button>
                        {!NARROWED ? (
                          <button type="button" className="dl-add" onClick={() => addCustom(scopeId, p.key)}>
                            + Add work item
                          </button>
                        ) : null}
                      </>,
                    )}
                  </div>
                </td>
              </tr>,
            );
            if (popen) matching.forEach(([it, idx]) => child.push(itemRow(scopeId, it, idx, null, 2)));
          });
      }

      if (NARROWED && !anyMatch) return;
      rows.push(
        <Fragment key={scopeId}>
          <tr className="scope-row" onClick={() => toggleScope(scopeId)}>
            <td className="ind">
              <i className="material-icons gcaret" style={{ transform: `rotate(${open ? 90 : 0}deg)` }}>
                chevron_right
              </i>
            </td>
            <td colSpan={cols - 1}>
              <div className="grp-cell lvl0">
                <span className="dl-dot" style={{ background: '#1976d2' }} />
                {`📌 ${op.opportunity_id}`}
                {op.name ? <span className="dl-oppname">{op.name}</span> : null}
                <span className="dl-pcount">{op.items.filter((i) => i.in_scope).length} in scope</span>
                {groupActions(
                  /*
                   * THREE-WAY, not two. "None" is a decision that this initiative has no
                   * delivery lifecycle — onboarding people, a contracting window, a single
                   * document — and it is distinct from not having chosen yet.
                   *
                   * A two-way toggle could not express it, so every initiative was forced
                   * through five Waterfall phases whether or not they meant anything.
                   */
                  <span className="meth-toggle">
                    <span
                      className={!isAgile && !isFlatMeth(op.methodology) ? 'on' : ''}
                      onClick={() => setOppMethodology(op.opportunity_id, 'Waterfall')}
                    >
                      Waterfall
                    </span>
                    <span
                      className={isAgile ? 'on' : ''}
                      onClick={() => setOppMethodology(op.opportunity_id, 'Agile')}
                    >
                      Agile
                    </span>
                    <span
                      className={isFlatMeth(op.methodology) ? 'on' : ''}
                      title="No phases — one flat Delivery window"
                      onClick={() => setOppMethodology(op.opportunity_id, 'None')}
                    >
                      None
                    </span>
                  </span>,
                )}
              </div>
            </td>
          </tr>
          {child}
        </Fragment>,
      );
    });
  }

  return (
    <div className="pm-wid">
      <HelpPanel help={withFinalizedRule(WID_HELP)} />
      <div className="wid-head">
        <div className="wid-titlerow">
          <i className="material-icons wid-titleicon">inventory_2</i>
          <div>
            <h1 className="page-header__title">Work Item Definition</h1>
            <p className="wid-sub">
              Set the program&apos;s global work items, then each opportunity&apos;s work items —
              Waterfall by phase or Agile.
            </p>
          </div>
        </div>

        <div className="dl-toolbar">
          <div className="dl-fg">
            <SelectField
              id="wid-project"
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={projectId}
              onChange={(v) => void onSelectProject(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>

          {prog ? (
            <div className="dl-fg" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="mth-tog">
                <button
                  type="button"
                  className={`mth wf${!SCOPED ? ' on' : ''}`}
                  title="Every work item in the catalogue, to pick from"
                  onClick={() => setScopeView('all')}
                >
                  All available
                </button>
                <button
                  type="button"
                  className={`mth wf${SCOPED ? ' on' : ''}`}
                  title="Only what this programme has taken on"
                  onClick={() => setScopeView('scoped')}
                >
                  In scope only
                </button>
              </span>
            </div>
          ) : null}
          {prog ? (
            <div className="dl-fg dl-filter-fg">
              <i className="material-icons" style={{ fontSize: 19, color: '#90a4ae' }}>
                search
              </i>
              <input
                className="dl-filter"
                type="text"
                placeholder="Filter work items…"
                autoComplete="off"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              {filterText ? (
                <button type="button" className="dl-filter-x" title="Clear filter" onClick={() => setFilterText('')}>
                  ✕
                </button>
              ) : null}
              {FILTERING ? (
                <span className="dl-filter-count">
                  {matchCount} match{matchCount === 1 ? '' : 'es'}
                </span>
              ) : null}
            </div>
          ) : null}

          {prog ? (
            <div className="wid-actions">
              <button type="button" className="link-btn" onClick={() => expandAll(true)}>
                Expand all
              </button>
              <button type="button" className="link-btn" onClick={() => expandAll(false)}>
                Collapse all
              </button>
              <span style={{ color: '#cfd8dc' }}>|</span>
              <span className="dl-summary">{inScopeTotal} in scope</span>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void save()}
                disabled={!dirty && saveLabel === 'Save'}
              >
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="wid-body">
        {!prog ? (
          <div className="wid-empty">
            <i className="material-icons ic">fact_check</i>
            <h3>Select a Project</h3>
            <p>
              Pick a program above to define its <strong>global work items</strong> and each{' '}
              <strong>opportunity&apos;s</strong> work items by phase.
            </p>
            {notice ? <p style={{ marginTop: 10, color: '#c62828', fontWeight: 600 }}>{notice}</p> : null}
          </div>
        ) : NARROWED && !rows.length ? (
          <div className="wid-nomatch">
            <i className="material-icons ic">search_off</i>
            {/* The two causes need different words: "nothing matches x" sends someone
                looking for a spelling mistake when the real answer is that nothing has
                been taken into scope yet. §7.1. */}
            {FILTERING ? (
              <h4>No work items match “{filterText}”.</h4>
            ) : (
              <h4>No work items are in scope for this programme yet.</h4>
            )}
            {!FILTERING ? (
              <p style={{ color: '#78909c', fontSize: 13, marginTop: 6 }}>
                Switch to <strong>All available</strong> to choose from the catalogue.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {notice ? (
              <div style={{ marginBottom: 10, color: '#c62828', fontWeight: 600, fontSize: 13 }}>{notice}</div>
            ) : null}
            <table className="dg">
              <colgroup>
                <col style={{ width: 44 }} />
                <col />
                <col style={{ width: 132 }} />
                {anyAgile ? <col style={{ width: 150 }} /> : null}
                <col style={{ width: 148 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 34 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="ind" />
                  <th>Work Item</th>
                  <th>Type</th>
                  {anyAgile ? <th>MVP wave</th> : null}
                  <th>File Type</th>
                  <th>Typical Owner</th>
                  <th>Stream</th>
                  <th>Start Date</th>
                  <th>Delivered By</th>
                  <th />
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </>
        )}
      </div>

      {toast ? <div className={`pm-wid-toast on ${toast.kind}`}>{toast.msg}</div> : null}
    </div>
  );
}
